<!--
 * Path: packages/frontend/src/core/components/dialogs/UserProfileDialog.vue
 * Task: Displays current user information, role breakdown, preferences, and a detailed recent activity stream.
 * Update: Extracted the project selector from the v-window to enable sticky positioning during scroll.
-->
<template>
  <BaseDialog
    v-model="authStore.isUserProfileDialogOpen"
    v-model:tabModel="activeTab"
    title="User Profile"
    max-width="650"
    @close="authStore.isUserProfileDialogOpen = false"
  >
    <!-- TAB HEADER INJECTION -->
    <template #tabs>
      <BaseTab value="profile" icon="mdi-account-outline">Profile</BaseTab>
      <BaseTab value="preferences" icon="mdi-cog-outline">Preferences</BaseTab>
      <BaseTab value="activity" icon="mdi-history">Activity Stream</BaseTab>
    </template>

    <!-- Activity Stream Project Selector (Rendered outside v-window so it can stick to the top!) -->
    <div v-if="activeTab === 'activity'" class="bg-surface" style="position: sticky; top: -24px; z-index: 10; margin: -24px -24px 0 -24px; padding: 24px 24px 16px 24px; border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));">
      <EntitySelect
        v-model="selectedActivityProjectId"
        :items="availableProjects"
        item-title="title"
        item-value="id"
        label="Project Context"
        placeholder="Select a project to view activity..."
        class="w-100"
        hide-details
      />
    </div>

    <!-- TAB CONTENT WINDOW -->
    <v-window v-model="activeTab" :class="{ 'pt-4': activeTab === 'activity', 'pt-2': activeTab !== 'activity' }" :touch="false">

      <!-- ============================================== -->
      <!-- TAB 1: PROFILE                                 -->
      <!-- ============================================== -->
      <v-window-item value="profile">
        <div v-if="authStore.user">
          <!-- Section 1: Personal Information -->
          <div class="text-caption font-weight-bold text-medium-emphasis text-uppercase mb-4 mt-2" style="letter-spacing: 1px;">
            Personal Information
          </div>
          <div class="d-flex align-start mb-2">
            <!-- Large Avatar -->
            <v-avatar size="72" color="primary" class="me-6 elevation-2 flex-shrink-0 mt-1">
              <span class="text-h4 text-white font-weight-bold">
                {{ (authStore.user.name || authStore.user.email).charAt(0).toUpperCase() }}
              </span>
            </v-avatar>

            <!-- Input Fields -->
            <div class="flex-grow-1">
              <v-text-field
                v-model="editableName"
                label="Full Name"
                prepend-inner-icon="mdi-account-edit-outline"
                variant="outlined"
                density="comfortable"
                class="mb-3"
                hide-details
              ></v-text-field>

              <v-text-field
                :model-value="authStore.user.email"
                label="Email Address"
                prepend-inner-icon="mdi-email-outline"
                variant="filled"
                density="comfortable"
                readonly
                hide-details
                bg-color="grey-lighten-4"
              ></v-text-field>

              <div class="d-flex justify-end mt-4">
                <v-btn
                  color="primary"
                  variant="flat"
                  class="font-weight-bold"
                  :disabled="!isNameDirty"
                  :loading="isSaving"
                  @click="saveName"
                >
                  Save Name
                </v-btn>
              </div>
            </div>
          </div>

          <v-divider class="my-6"></v-divider>

          <!-- Section 2: Roles and Capabilities -->
          <div class="text-caption font-weight-bold text-medium-emphasis text-uppercase mb-4" style="letter-spacing: 1px;">
            Assigned Roles & Capabilities
          </div>
          <v-row>
            <v-col cols="12" sm="6">
              <v-card variant="flat" class="pa-4 bg-grey-lighten-4 h-100 rounded-lg text-center d-flex flex-column align-center justify-center">
                <div class="text-caption font-weight-bold text-medium-emphasis text-uppercase mb-2">Global System Role</div>
                <v-chip size="small" :color="authStore.isAppAdmin ? 'error' : 'primary'" variant="flat" class="font-weight-bold text-uppercase">
                  {{ authStore.isAppAdmin ? 'App Administrator' : 'Registered User' }}
                </v-chip>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6">
              <v-card variant="flat" class="pa-4 bg-grey-lighten-4 h-100 rounded-lg text-center d-flex flex-column align-center justify-center">
                <div class="text-caption font-weight-bold text-medium-emphasis text-uppercase mb-2">Role in Active Project</div>
                <v-chip v-if="activeProjectRole" size="small" :color="getProjectRoleColor(activeProjectRole)" variant="flat" class="font-weight-bold text-uppercase">
                  {{ activeProjectRole }}
                </v-chip>
                <div v-else class="text-caption font-italic text-disabled">No project loaded</div>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </v-window-item>

      <!-- ============================================== -->
      <!-- TAB 2: PREFERENCES                             -->
      <!-- ============================================== -->
      <v-window-item value="preferences">
        <div class="text-caption font-weight-bold text-medium-emphasis text-uppercase mb-4 mt-2" style="letter-spacing: 1px;">
          App Settings
        </div>

        <v-list class="bg-transparent pa-0">
          <!-- UI Theme Toggle -->
          <v-list-item class="px-0 mb-4">
            <template v-slot:prepend>
              <v-icon color="medium-emphasis" class="me-4">mdi-theme-light-dark</v-icon>
            </template>
            <v-list-item-title class="font-weight-bold text-body-1">Design Theme</v-list-item-title>
            <v-list-item-subtitle class="text-caption text-medium-emphasis">Choose your preferred visual style.</v-list-item-subtitle>

            <template v-slot:append>
              <v-btn-toggle
                v-model="authStore.visibilityMode"
                density="compact"
                mandatory
                color="primary"
                @update:model-value="authStore.setVisibilityMode"
                class="border"
                style="height: 32px;"
              >
                <v-btn value="light" variant="text" title="Light Mode" class="px-3">
                  <v-icon size="small">mdi-white-balance-sunny</v-icon>
                </v-btn>
                <v-btn value="auto" variant="text" title="System Theme" class="px-3">
                  <v-icon size="small">mdi-brightness-auto</v-icon>
                </v-btn>
                <v-btn value="dark" variant="text" title="Dark Mode" class="px-3">
                  <v-icon size="small">mdi-weather-night</v-icon>
                </v-btn>
              </v-btn-toggle>
            </template>
          </v-list-item>

          <v-divider class="mb-4"></v-divider>

          <!-- Sidebar Toggle -->
          <v-list-item class="px-0">
            <template v-slot:prepend>
              <v-icon color="medium-emphasis" class="me-4">mdi-dock-left</v-icon>
            </template>
            <v-list-item-title class="font-weight-bold text-body-1">Compact Sidebar</v-list-item-title>
            <v-list-item-subtitle class="text-caption text-medium-emphasis">Minimize the main navigation menu by default.</v-list-item-subtitle>

            <template v-slot:append>
              <v-switch
                v-model="authStore.isSidebarMinimized"
                color="primary"
                density="compact"
                hide-details
                inset
                @update:model-value="authStore.setSidebarMode"
              ></v-switch>
            </template>
          </v-list-item>
        </v-list>
      </v-window-item>

      <!-- ============================================== -->
      <!-- TAB 3: ACTIVITY STREAM                         -->
      <!-- ============================================== -->
      <v-window-item value="activity">
        <!-- Loading State -->
        <div v-if="isLoadingActivity" class="d-flex justify-center py-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <!-- Empty State -->
        <div v-else-if="activityStream.length === 0" class="text-center pa-6 text-medium-emphasis bg-grey-lighten-4 rounded-lg">
          <v-icon size="32" class="mb-2 opacity-50">mdi-clock-remove-outline</v-icon>
          <div class="text-body-2">No recent edits or comments found in the selected project.</div>
        </div>

        <!-- Activity Feed -->
        <v-list v-else lines="two" class="bg-transparent pa-0">
          <v-list-item
            v-for="activity in activityStream"
            :key="activity.id + activity.datetime"
            class="mb-3 border rounded-lg bg-surface pt-3 pb-3"
          >
            <!-- Left Icon/Avatar with Context Badge -->
            <template v-slot:prepend>
              <div class="position-relative me-4 mb-auto" style="margin-top: 2px;">
                <!-- Native Entity Icon (Shape, Color, Symbol) with Tooltip -->
                <div style="cursor: help;">
                  <EntityIcon :entity="{ entityType: activity.entityType }" size="36" />
                  <v-tooltip activator="parent" location="top">
                    {{ entityInfoMap[activity.entityType]?.title || activity.entityType }}
                  </v-tooltip>
                </div>
                <!-- Action Badge (Edit vs Comment) -->
                <v-avatar
                  size="18"
                  :color="activity.type === 'comment' ? 'primary' : 'grey-darken-2'"
                  class="position-absolute elevation-1"
                  style="bottom: -4px; right: -4px; border: 2px solid rgb(var(--v-theme-surface));"
                >
                  <v-icon color="white" :size="10">
                    {{ activity.type === 'comment' ? 'mdi-comment' : 'mdi-pencil' }}
                  </v-icon>
                </v-avatar>
              </div>
            </template>

            <!-- Main Title & Entity Context -->
            <v-list-item-title class="font-weight-medium text-body-2 mb-1 d-flex align-center flex-wrap" style="gap: 6px;">
              <span class="font-weight-bold">{{ activity.type === 'comment' ? 'Commented on' : 'Edited' }}</span>
              <span class="text-high-emphasis">{{ activity.entityTitle }}</span>
              <v-chip size="x-small" variant="tonal" class="text-uppercase font-weight-bold text-medium-emphasis">
                {{ entityInfoMap[activity.entityType]?.title || activity.entityType }}
              </v-chip>
            </v-list-item-title>

            <!-- Timestamp -->
            <v-list-item-subtitle class="text-caption text-medium-emphasis mb-3">
              {{ formatTime(activity.datetime) }}
            </v-list-item-subtitle>

            <!-- Details Area (Comments text or Diff history) -->
            <div class="mt-2 w-100">
              <!-- For Comments -->
              <template v-if="activity.type === 'comment'">
                <div class="text-body-2 bg-grey-lighten-4 pa-2 rounded" style="white-space: pre-wrap; line-height: 1.4;">"{{ activity.text }}"</div>
              </template>

              <!-- For Edits -->
              <template v-else-if="activity.changes && activity.changes.length > 0">
                <div
                  v-for="(change, idx) in activity.changes"
                  :key="change.field + '-' + idx"
                  class="mb-3 text-body-2"
                >
                  <!-- Field Name -->
                  <div class="font-weight-bold mb-1 text-high-emphasis">
                    {{ formatFieldName(change.field) }}
                  </div>

                  <!-- Creation Event -->
                  <template v-if="change.field === 'system.creation'">
                    <div class="d-flex align-start text-caption mt-1" style="gap: 4px;">
                      <v-icon size="14" color="primary" class="mt-1 flex-shrink-0">mdi-star-four-points-outline</v-icon>
                      <span class="text-primary font-weight-bold" style="word-break: break-word; line-height: 1.3;">
                        Entity was created
                      </span>
                    </div>
                  </template>

                  <!-- Diff Values -->
                  <template v-else>
                    <div class="d-flex align-start text-caption" style="gap: 4px;">
                      <v-icon size="14" color="error" class="mt-1 flex-shrink-0">mdi-minus-box-outline</v-icon>
                      <span class="text-error" style="text-decoration: line-through; word-break: break-word; line-height: 1.3;">
                        {{ formatValue(change.oldValue) }}
                      </span>
                    </div>
                    <div class="d-flex align-start text-caption mt-1" style="gap: 4px;">
                      <v-icon size="14" color="success" class="mt-1 flex-shrink-0">mdi-plus-box-outline</v-icon>
                      <span class="text-success" style="word-break: break-word; line-height: 1.3;">
                        {{ formatValue(change.newValue) }}
                      </span>
                    </div>
                  </template>
                </div>
              </template>
            </div>
          </v-list-item>
        </v-list>
      </v-window-item>
    </v-window>
  </BaseDialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useAuthStore } from '@/store/auth';
import { useProjectStore } from '@/store/project';
import { useSelectOptions } from '@/core/composables/useSelectOptions';
import { api } from '@/core/services/api';
import type { Entity } from '@/core/types/editor';
import BaseDialog from './BaseDialog.vue';
import BaseTabbar from '@/core/components/common/baseTab/BaseTabbar.vue';
import BaseTab from '@/core/components/common/baseTab/BaseTab.vue';
import EntityIcon from '@/core/components/common/EntityIcon.vue';
import EntitySelect from '@/core/components/common/EntitySelect.vue';

const authStore = useAuthStore();
const projectStore = useProjectStore();
const { entityInfoMap } = useSelectOptions();

// Tab State
const activeTab = ref('profile');

// Editable Profile State
const editableName = ref('');
const isSaving = ref(false);

// Activity Stream State
const selectedActivityProjectId = ref<string | null>(null);
const isLoadingActivity = ref(false);
const activityStream = ref<any[]>([]);
const activityNodesMap = ref(new Map<string, any>());

// Map available projects for the dropdown
const availableProjects = computed(() => {
  const meta = projectStore.projectsMetadata || {};
  return Object.entries(meta).map(([id, p]) => ({
    id,
    title: p.displayName || id,
    color: p.color || '#424242',
    icon: p.icon || 'mdi-cube',
    entityType: 'projects'
  })).sort((a, b) => a.title.localeCompare(b.title));
});

// Reset internal state when the dialog is opened
watch(() => authStore.isUserProfileDialogOpen, (isOpen) => {
  if (isOpen) {
    editableName.value = authStore.user?.name || '';

    // Automatically select the active project, or fallback to the first available project
    if (!selectedActivityProjectId.value) {
      if (projectStore.loadedProject?.value && availableProjects.value.some(p => p.id === projectStore.loadedProject?.value)) {
        selectedActivityProjectId.value = projectStore.loadedProject.value;
      } else if (availableProjects.value.length > 0) {
        selectedActivityProjectId.value = availableProjects.value[0].id;
      }
    }
  }
});

const isNameDirty = computed(() => {
  return editableName.value.trim() !== (authStore.user?.name || '');
});

const saveName = async () => {
  if (!isNameDirty.value) return;
  isSaving.value = true;
  try {
    await authStore.updateProfile(editableName.value.trim());
  } finally {
    isSaving.value = false;
  }
};

const activeProjectRole = computed(() => {
  const loaded = projectStore.loadedProject?.value;
  if (!loaded || !projectStore.projectsMetadata || !authStore.user) return null;
  const meta = projectStore.projectsMetadata[loaded];
  if (!meta || !meta.permissions) return 'Guest';

  const email = authStore.user.email.toLowerCase();
  if ((meta.permissions.projectAdmin || []).map((e: string) => e.toLowerCase()).includes(email)) return 'Project Administrator';
  if ((meta.permissions.author || []).map((e: string) => e.toLowerCase()).includes(email)) return 'Author';
  if ((meta.permissions.reader || []).map((e: string) => e.toLowerCase()).includes(email)) return 'Reader';
  return 'Guest';
});

const getProjectRoleColor = (role: string) => {
  if (role === 'Project Administrator') return 'error';
  if (role === 'Author') return 'warning';
  if (role === 'Reader') return 'success';
  return 'grey';
};

// Fetch real-time activity for the selected project without overriding the global pinia datastore
watch(selectedActivityProjectId, async (newProjectId) => {
  if (!newProjectId) {
    activityStream.value = [];
    return;
  }

  isLoadingActivity.value = true;
  try {
    const response = await api.datastore.get(newProjectId);
    const nodes = response.nodes || [];

    // Maintain a local map purely to resolve entity titles in formatValue()
    const nodeMap = new Map();
    nodes.forEach(n => nodeMap.set(n.id, n));
    activityNodesMap.value = nodeMap;

    const email = authStore.user?.email;
    const activities: any[] = [];

    nodes.forEach(node => {
      // Check Edits
      if (node.history && Array.isArray(node.history)) {
        node.history.forEach((h: any) => {
          if (h.author === email) {
            activities.push({
              type: 'edit',
              id: h.id,
              entityId: node.id,
              entityType: node.type,
              entityTitle: node.title || node.id,
              datetime: h.datetime,
              changes: h.changes
            });
          }
        });
      }
      // Check Comments
      if (node.comments && Array.isArray(node.comments)) {
        node.comments.forEach((c: any) => {
          if (c.author === email) {
            activities.push({
              type: 'comment',
              id: c.id,
              entityId: node.id,
              entityType: node.type,
              entityTitle: node.title || node.id,
              datetime: c.datetime,
              text: c.text
            });
          }
        });
      }
    });

    // Sort descending (newest first) and limit to top 50 events
    activityStream.value = activities
      .sort((a, b) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime())
      .slice(0, 50);

  } catch (e) {
    console.error("Failed to load activity stream", e);
    activityStream.value = [];
  } finally {
    isLoadingActivity.value = false;
  }
}, { immediate: true });


const formatTime = (isoStr: string) => {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

// --- History Formatting Helpers (Consistent with FloatingPanelHistory) ---
const formatFieldName = (field: string): string => {
  if (field === 'system.creation') return 'Creation';
  if (field.startsWith('raci.')) return `RACI: ${field.replace('raci.', '')}`;
  return field.charAt(0).toUpperCase() + field.slice(1);
};

const formatValue = (val: any): string => {
  if (val === null || val === undefined || val === '') return '(empty)';

  if (Array.isArray(val)) {
    if (val.length === 0) return '(empty)';
    return val.map((id: string) => {
      const entity = activityNodesMap.value.get(id);
      return entity ? (entity.title || id) : id;
    }).join(', ');
  }

  if (typeof val === 'object') return JSON.stringify(val);

  // Resolve long IDs to entity titles for readability
  if (typeof val === 'string' && val.length > 20 && val.includes('-')) {
    const entity = activityNodesMap.value.get(val);
    if (entity) return entity.title || val;
  }

  return String(val);
};
</script>

<style scoped>
.min-height-0 {
  min-height: 0 !important;
}
</style>
